import { Box, Container, CssBaseline } from '@mui/material'
import { useRecoilValue } from 'recoil'

import { useDeskForm } from '../../hooks/useDeskForm'
import { sessionState } from '../../state'
import Database from '../../types/Database'

import CreateDeskHeader from './CreateDeskHeader'
import DeskForm from './DeskForm'

interface IDeskProps {
  user: Database.User | null
  desk: Database.Desk | null
  onCreate: () => void
  onUpdate: (deskId: string) => void
  preSelectedOffice?: Database.Office | null
  onCancel?: () => void
}

/**
 * CreateDesk - Container component for desk creation/editing
 * Uses useDeskForm hook for form management and DeskService for business operations
 * Coordinates between form and service layers
 */
export default function CreateDesk(props: IDeskProps) {
  const session = useRecoilValue(sessionState)

  // Use the custom hook for form state management
  const {
    formData,
    isSubmitting,
    validationErrors,
    successMessage,
    updateField,
    submitForm,
    clearMessage
  } = useDeskForm({
    user: props.user,
    desk: props.desk,
    onCreate: props.onCreate,
    onUpdate: props.onUpdate,
    preSelectedOffice: props.preSelectedOffice
  })

  return (
    <Container
      component='main'
      maxWidth='sm'
      sx={{
        backgroundColor: 'background.default',
        minHeight: '100vh'
      }}
    >
      <CssBaseline />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          minHeight: '100vh'
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 600,
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            border: '1px solid',
            borderColor: 'divider',
            backdropFilter: 'blur(10px)',
            p: 4,
            mb: 4
          }}
        >
          <CreateDeskHeader user={props.user} desk={props.desk} />

          <DeskForm
            formData={formData}
            isEditMode={!!props.desk}
            isSubmitting={isSubmitting}
            availableOffices={session.offices || []}
            validationErrors={validationErrors}
            successMessage={successMessage}
            onSubmit={submitForm}
            onChange={updateField}
            onClearMessage={clearMessage}
            preSelectedOffice={props.preSelectedOffice}
            onCancel={props.onCancel}
          />
        </Box>
      </Box>
    </Container>
  )
}
